import {createSelector} from 'reselect';
import {
    getTranslations
} from './store';

/* eslint-disable complexity */
import {convertToLocaleTimeString} from '../utils/timeUtils';
import guardedSelector from '../utils/guardedSelector';
import {getListingHouseRules, getLegacyLodgingPolicy} from './listing';
import {getLocaleFormatted} from './system';

const getBinaryHouseRules = createSelector(
    [getListingHouseRules, getTranslations],
    (listingHouseRules = {}, trans = {}) => {
        const binaryHouseRules = [];

        if (listingHouseRules.eventsAllowedRule) {
            binaryHouseRules.push({
                allowed: listingHouseRules.eventsAllowedRule.allowed,
                label: listingHouseRules.eventsAllowedRule.allowed ? trans.hrEventsAllowed : trans.hrEventsNotAllowed,
                note: listingHouseRules.eventsAllowedRule.note
            });
        }

        if (listingHouseRules.smokingAllowedRule) {
            binaryHouseRules.push({
                allowed: listingHouseRules.smokingAllowedRule.allowed,
                label: listingHouseRules.smokingAllowedRule.allowed ? trans.hrSmokingAllowed : trans.hrSmokingNotAllowed,
                note: listingHouseRules.smokingAllowedRule.note
            });
        }

        if (listingHouseRules.petsAllowedRule) {
            binaryHouseRules.push({
                allowed: listingHouseRules.petsAllowedRule.allowed,
                label: listingHouseRules.petsAllowedRule.allowed ? trans.hrPetsAllowed : trans.hrPetsNotAllowed,
                note: listingHouseRules.petsAllowedRule.note
            });
        }

        if (listingHouseRules.childrenAllowedRule) {
            binaryHouseRules.push({
                allowed: listingHouseRules.childrenAllowedRule.allowed,
                label: listingHouseRules.childrenAllowedRule.allowed ? trans.hrChildrenAllowed : trans.hrChildrenNotAllowed,
                note: listingHouseRules.childrenAllowedRule.note
            });
        }

        return binaryHouseRules;
    }
);
/* eslint-enable complexity */

const getValueHouseRules = createSelector(
    [getListingHouseRules, getTranslations],
    (listingHouseRules = {}, trans = {}) => {
        const valueRules = [];

        if (listingHouseRules.minimumAgeRule) {
            valueRules.push({
                value: listingHouseRules.minimumAgeRule.age,
                label: `${trans.hrMinAge}:`,
                note: listingHouseRules.minimumAgeRule.note
            });
        }

        if (listingHouseRules.maximumOccupancyRule) {
            let value = listingHouseRules.maximumOccupancyRule.guests;
            if (value && listingHouseRules.maximumOccupancyRule.adults) {
                value += ` (${listingHouseRules.maximumOccupancyRule.adults} ${trans.adultPlural})`;
            }

            valueRules.push({
                value,
                label: `${trans.hrMaxOcc}:`,
                note: listingHouseRules.maximumOccupancyRule.note
            });
        }

        return valueRules;
    }
);

const getCustomHouseRules = createSelector(
    [getListingHouseRules],
    (listingHouseRules = {}) => {
        return [
            listingHouseRules.customHouseRule01,
            listingHouseRules.customHouseRule02,
            listingHouseRules.customHouseRule03
        ].filter((rule) => {
            return !!(rule && rule.note);
        });
    }
);

const getHouseRules = guardedSelector('getHouseRules', createSelector(
    [getBinaryHouseRules, getValueHouseRules, getCustomHouseRules, getListingHouseRules, getLegacyLodgingPolicy, getLocaleFormatted],
    (binaryRules, valueRules, customRules, listingHouseRules, legacyLodgingPolicy, locale) => {
        // we only want check in/out from legacy rules
        const safeHouseRules = listingHouseRules || legacyLodgingPolicy || {};
        const {checkInTime, checkOutTime} = safeHouseRules;
        const showHouseRulesSection = checkInTime && checkOutTime;

        // if property is using legacy, unstructured house rules we can short circuit
        if (!listingHouseRules) {
            return {
                showHouseRulesSection: !!showHouseRulesSection,
                displayHouseRules: false,
                checkIn: convertToLocaleTimeString(checkInTime, locale),
                checkOut: convertToLocaleTimeString(checkOutTime, locale)
            };
        }

        return {
            showHouseRulesSection: true,
            displayHouseRules: !!(binaryRules.length + valueRules.length + customRules.length),
            checkIn: convertToLocaleTimeString(checkInTime, locale),
            checkOut: convertToLocaleTimeString(checkOutTime, locale),
            binaryRules,
            valueRules,
            customRules
        };
    }
));
/* eslint-enable complexity */

export default getHouseRules;
export {
    getHouseRules,
    getBinaryHouseRules,
    getValueHouseRules,
    getCustomHouseRules
};
